import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, CircularProgress, Alert, Chip, Stack, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Divider, FormControlLabel, Checkbox, List, ListItem, ListItemText,
} from '@mui/material';
import {
  Inventory2 as AssetIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Visibility as ViewIcon, QrCode2 as QrIcon, Category as TypesIcon, Close as CloseIcon,
  Build as MaintIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const STATUS = {
  in_stock:  { label: 'На складе',       color: 'info' },
  installed: { label: 'Установлен',      color: 'success' },
  reserved:  { label: 'Зарезервирован',  color: 'warning' },
  in_repair: { label: 'В ремонте',       color: 'warning' },
  retired:   { label: 'Списан',          color: 'default' },
  lost:      { label: 'Утерян',          color: 'error' },
};
const STATUS_OPTS = Object.entries(STATUS).map(([v, o]) => ({ v, t: o.label }));
const EVENT_T = {
  received: 'Поступление', stocked: 'На склад', reserved: 'Резерв', installed: 'Установка',
  removed: 'Снятие', moved: 'Перемещение', repair: 'Ремонт', maintenance: 'Обслуживание',
  retired: 'Списание', lost: 'Утеря', updated: 'Изменение',
};

function Assets() {
  const { user } = useAuth();
  const can = (p) => user?.is_superuser || user?.permissions?.includes(p);
  const canManage = can('assets.manage');

  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);     // create/edit
  const [detail, setDetail] = useState(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [maint, setMaint] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get('/assets/', { params: { status_filter: statusFilter || undefined, q: q || undefined } });
      setRows(res.data.items || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  const loadRefs = useCallback(async () => {
    try {
      const [t, l, d] = await Promise.all([
        axios.get('/assets/types'),
        axios.get('/locations/'),
        axios.get('/devices/', { params: { limit: 1000 } }),
      ]);
      setTypes(t.data || []);
      setLocations(l.data.items || l.data || []);
      setDevices(d.data.items || d.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const del = async (a) => {
    if (!window.confirm(`Удалить актив «${a.asset_tag}»? История будет потеряна.`)) return;
    try { await axios.delete(`/assets/${a.id}`); await load(); }
    catch (err) { setError(err.response?.data?.detail || err.message); }
  };

  const openDetail = async (a) => {
    try { const r = await axios.get(`/assets/${a.id}`); setDetail(r.data); }
    catch (err) { setError(err.response?.data?.detail || err.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssetIcon color="primary" />
          <Typography variant="h4">Активы</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {canManage && <Button variant="outlined" startIcon={<TypesIcon />} onClick={() => setTypesOpen(true)}>Типы активов</Button>}
          {canManage && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({ status: 'in_stock' })}>Добавить актив</Button>}
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Активы — это конкретные физические единицы (планки ОЗУ, диски, ПК) с серийником, статусом и <b>личной историей</b>.
        Каждое перемещение, установка и списание фиксируется в журнале актива.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField select size="small" label="Статус" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">Все статусы</MenuItem>
          {STATUS_OPTS.map((s) => <MenuItem key={s.v} value={s.v}>{s.t}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Поиск (тег / серийник / модель)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 300 }} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">Активов нет. {canManage && 'Нажмите «Добавить актив».'}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Инв. тег', 'Тип', 'Серийный №', 'Модель', 'Статус', 'Где', 'Действия'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold' }} align={h === 'Действия' ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => {
                const st = STATUS[a.status] || { label: a.status, color: 'default' };
                const where = a.device_name ? `в ${a.device_name}` : (a.location_name || '—');
                return (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ cursor: 'pointer', fontWeight: 500 }} onClick={() => openDetail(a)}>{a.asset_tag}</TableCell>
                    <TableCell>{a.asset_type_name}</TableCell>
                    <TableCell>{a.serial_number || '—'}</TableCell>
                    <TableCell>{a.model || '—'}</TableCell>
                    <TableCell><Chip size="small" label={st.label} color={st.color} /></TableCell>
                    <TableCell>{where}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Подробнее / QR"><IconButton size="small" onClick={() => openDetail(a)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      {canManage && <Tooltip title="Редактировать"><IconButton size="small" onClick={() => setForm(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                      {canManage && <Tooltip title="Записать обслуживание"><IconButton size="small" onClick={() => setMaint(a)}><MaintIcon fontSize="small" /></IconButton></Tooltip>}
                      {canManage && <Tooltip title="Удалить"><IconButton size="small" color="error" onClick={() => del(a)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {form && (
        <AssetForm initial={form} types={types} locations={locations} devices={devices}
          onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} setError={setError} />
      )}
      {detail && <DetailDialog asset={detail} onClose={() => setDetail(null)} />}
      {typesOpen && <TypesDialog types={types} onClose={() => setTypesOpen(false)} onChanged={loadRefs} setError={setError} />}
      {maint && <MaintenanceDialog asset={maint} onClose={() => setMaint(null)} onDone={() => { setMaint(null); load(); }} setError={setError} />}
    </Box>
  );
}

function AssetForm({ initial, types, locations, devices, onClose, onSaved, setError }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    asset_tag: initial.asset_tag || '', serial_number: initial.serial_number || '',
    asset_type_id: initial.asset_type_id || '', model: initial.model || '', vendor: initial.vendor || '',
    status: initial.status || 'in_stock', current_location_id: initial.current_location_id || '',
    installed_in_device_id: initial.installed_in_device_id || '',
    warranty_until: initial.warranty_until || '', eol_date: initial.eol_date || '',
    is_critical: !!initial.is_critical, notes: initial.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = { ...f };
      ['current_location_id', 'installed_in_device_id', 'asset_type_id'].forEach((k) => { if (payload[k] === '') payload[k] = null; });
      ['warranty_until', 'eol_date'].forEach((k) => { if (!payload[k]) payload[k] = null; });
      if (isEdit) await axios.put(`/assets/${initial.id}`, payload);
      else await axios.post('/assets/', payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? `Актив «${initial.asset_tag}»` : 'Новый актив'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Инвентарный тег" value={f.asset_tag} onChange={set('asset_tag')} required sx={{ flex: 1 }} />
            <TextField label="Серийный номер" value={f.serial_number} onChange={set('serial_number')} sx={{ flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Тип актива" value={f.asset_type_id} onChange={set('asset_type_id')} required sx={{ flex: 1 }}>
              <MenuItem value="">— выберите —</MenuItem>
              {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
            <TextField select label="Статус" value={f.status} onChange={set('status')} sx={{ flex: 1 }}>
              {STATUS_OPTS.map((s) => <MenuItem key={s.v} value={s.v}>{s.t}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Производитель" value={f.vendor} onChange={set('vendor')} sx={{ flex: 1 }} />
            <TextField label="Модель" value={f.model} onChange={set('model')} sx={{ flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Локация (склад/место)" value={f.current_location_id} onChange={set('current_location_id')} sx={{ flex: 1 }}>
              <MenuItem value="">— нет —</MenuItem>
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </TextField>
            <TextField select label="Установлен в устройство" value={f.installed_in_device_id} onChange={set('installed_in_device_id')} sx={{ flex: 1 }}>
              <MenuItem value="">— нет —</MenuItem>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Гарантия до" value={f.warranty_until || ''} onChange={set('warranty_until')} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            <TextField type="date" label="Конец срока службы (EOL)" value={f.eol_date || ''} onChange={set('eol_date')} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Stack>
          <TextField label="Заметки" value={f.notes} onChange={set('notes')} multiline minRows={1} />
          <FormControlLabel control={<Checkbox checked={f.is_critical} onChange={(e) => setF((x) => ({ ...x, is_critical: e.target.checked }))} />}
            label="Критичный актив" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={busy || !f.asset_tag || !f.asset_type_id}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ asset, onClose }) {
  const st = STATUS[asset.status] || { label: asset.status, color: 'default' };
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Актив «{asset.asset_tag}»
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <QRCodeSVG value={asset.asset_tag} size={140} level="M" includeMargin />
            <Typography variant="caption" display="block">{asset.asset_tag}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={st.label} color={st.color} />
              <Chip size="small" variant="outlined" label={asset.asset_type_name} />
              {asset.is_critical && <Chip size="small" color="error" label="критичный" />}
              {asset.next_service_date && <Chip size="small" color="warning" variant="outlined" label={`След. ТО: ${asset.next_service_date}`} />}
            </Stack>
            <Typography variant="body2">Серийный №: {asset.serial_number || '—'}</Typography>
            <Typography variant="body2">Производитель/модель: {asset.vendor || '—'} {asset.model || ''}</Typography>
            <Typography variant="body2">Где: {asset.device_name ? `в устройстве ${asset.device_name}` : (asset.location_name || '—')}</Typography>
            <Typography variant="body2">Гарантия до: {asset.warranty_until || '—'} · EOL: {asset.eol_date || '—'}</Typography>
            {asset.notes && <Typography variant="body2" sx={{ mt: 1 }}>{asset.notes}</Typography>}
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>История ({asset.events?.length || 0})</Typography>
        <List dense>
          {(asset.events || []).map((e) => (
            <ListItem key={e.id} sx={{ borderLeft: '3px solid #1976d2', mb: 0.5, bgcolor: '#f8fafc' }}>
              <ListItemText
                primary={`${EVENT_T[e.event_type] || e.event_type}${e.reason ? ' — ' + e.reason : ''}`}
                secondary={`${new Date(e.created_at).toLocaleString('ru-RU')}${e.performed_by ? ' · ' + e.performed_by.full_name : ''}${e.change_request_id ? ' · заявка #' + e.change_request_id : ''}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
  );
}

function TypesDialog({ types, onClose, onChanged, setError }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const add = async () => {
    if (!name.trim()) return;
    try { await axios.post('/assets/types', { name: name.trim(), description: desc || null }); setName(''); setDesc(''); onChanged(); }
    catch (err) { setError(err.response?.data?.detail || err.message); }
  };
  const del = async (t) => {
    try { await axios.delete(`/assets/types/${t.id}`); onChanged(); }
    catch (err) { setError(err.response?.data?.detail || err.message); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Типы активов</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField size="small" label="Название" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1 }} />
          <TextField size="small" label="Описание" value={desc} onChange={(e) => setDesc(e.target.value)} sx={{ flex: 1 }} />
          <Button variant="contained" onClick={add} disabled={!name.trim()}>Добавить</Button>
        </Stack>
        <List dense>
          {types.map((t) => (
            <ListItem key={t.id} secondaryAction={
              <Tooltip title={t.asset_count ? 'Есть активы — удалить нельзя' : 'Удалить'}>
                <span><IconButton edge="end" size="small" color="error" disabled={!!t.asset_count} onClick={() => del(t)}><DeleteIcon fontSize="small" /></IconButton></span>
              </Tooltip>
            }>
              <ListItemText primary={`${t.name}${t.asset_count ? ` (${t.asset_count})` : ''}`} secondary={t.description} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
  );
}

function MaintenanceDialog({ asset, onClose, onDone, setError }) {
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState('maintenance');
  const [cost, setCost] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`/assets/${asset.id}/maintenance`, {
        reason, kind,
        cost: cost === '' ? null : Number(cost),
        next_service_date: nextDate || null,
      });
      onDone();
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Обслуживание / ремонт — {asset.asset_tag}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Тип" value={kind} onChange={(e) => setKind(e.target.value)}>
            <MenuItem value="maintenance">Плановое ТО</MenuItem>
            <MenuItem value="repair">Ремонт</MenuItem>
          </TextField>
          <TextField label="Что сделано / причина" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} required />
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Стоимость" value={cost} onChange={(e) => setCost(e.target.value)} sx={{ flex: 1 }} />
            <TextField type="date" label="Следующее ТО" value={nextDate} onChange={(e) => setNextDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={busy || !reason.trim()}>Записать</Button>
      </DialogActions>
    </Dialog>
  );
}

export default Assets;
